import { useEffect, useRef } from 'react'
import type { VirtuosoHandle } from 'react-virtuoso'

import type { ChatItem } from './chat-message.interface'

interface UseStreamingAutoScrollOptions {
  virtuosoRef: React.RefObject<VirtuosoHandle | null>

  itemList: ChatItem[]

  isEnabled?: boolean
}

const getStreamingContentHash = (itemList: ChatItem[]): string => {
  const streamingItems = itemList.filter((item) => 'isStreaming' in item && item.isStreaming)

  if (streamingItems.length === 0) {
    return ''
  }

  return streamingItems
    .map((item) => {
      const content = 'content' in item ? item.content : ''
      return `${item.id}:${content.length}`
    })
    .join('|')
}

export const useStreamingAutoScroll = ({
  virtuosoRef,
  itemList,
  isEnabled = true,
}: UseStreamingAutoScrollOptions): {
  handleAtBottomStateChange: (atBottom: boolean) => void

  isAtBottom: boolean
} => {
  const isAtBottomRef = useRef(true)
  const prevStreamingHashRef = useRef('')

  const handleAtBottomStateChange = (atBottom: boolean): void => {
    isAtBottomRef.current = atBottom
  }

  useEffect(() => {
    if (!isEnabled) return

    const currentHash = getStreamingContentHash(itemList)
    const hasStreamingContent = currentHash !== ''
    const contentChanged = currentHash !== prevStreamingHashRef.current

    prevStreamingHashRef.current = currentHash

    if (hasStreamingContent && contentChanged && isAtBottomRef.current) {
      virtuosoRef.current?.scrollToIndex({
        index: 'LAST',
        align: 'end',
        behavior: 'auto',
      })
    }
  }, [itemList, isEnabled, virtuosoRef])

  return {
    handleAtBottomStateChange,
    isAtBottom: isAtBottomRef.current,
  }
}
